import { useEffect, useMemo } from "react";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { type AgencyAvailabilityInput, getAgencyFacilityIdMap, getAvailableAgencies, getSuppressedAgencies } from "../utils/agencyIntegrations";
import { claimAgencySuppressionReport } from "../utils/agencySuppressionReports";
import type { AgencySource } from "../utils/models";

export type AgencyAvailability = {
	/** Agencies the chosen department has a facility id for AND has not deactivated, in enum order. */
	availableAgencies: AgencySource[];
	/** Every agency's facility id, whether or not the integration is active. */
	agencyFacilityIdMap: Record<AgencySource, string | undefined>;
};

/**
 * The chosen department's agency availability — the one place the create flow, the post-to-agency
 * dialog and the Requests-tab eligibility gate read it from.
 *
 * Both results are memoized on a primitive key derived from the agency rows. That is load-bearing
 * rather than a nicety: `PostToAgencyDialog` has a `useEffect` keyed on the agency
 * list (`PostToAgencyDialog.tsx:78-81`) which RESETS the admin's chosen agency, so a fresh array
 * identity per render would wipe their selection mid-dialog.
 */
export const useAgencyAvailability = (): AgencyAvailability => {
	const { chosenDepartment } = useHospitalData();
	const { track } = useAnalytics();
	// Widening to the structural input type (a plain assignment, not a cast) is what lets this hook read
	// the seam; `Department` declares `agencyIntegrations`, so this is a narrowing view, not a hole.
	const department: AgencyAvailabilityInput = chosenDepartment;
	const { agencyIntegrations } = department;

	/**
	 * A PRIMITIVE stand-in for the `agencyIntegrations` array, and the reason the memos below are safe.
	 *
	 * The array is a fresh object on every `setChosenDepartment`, so keying a memo on its reference
	 * recomputes on every render and hands `PostToAgencyDialog` a new `availableAgencies` identity each
	 * time — which re-fires its reset effect and clears the admin's agency mid-dialog. Sorted, so a
	 * server-side re-ordering of two unchanged rows is not mistaken for a change. Same problem and same
	 * solution as `checkboxSignature` in `components/UIToolkit/Popups/Dialog.tsx:202-214`.
	 *
	 * Only `source` and `active` participate: they are the whole of what availability is derived from.
	 * `facilityId` is deliberately included too, because `getAgencyFacilityIdMap` now reads it.
	 */
	const agencyIntegrationsKey = (agencyIntegrations ?? [])
		.map((entry) => `${entry.source}:${entry.active}:${entry.facilityId}`)
		.sort()
		.join(",");

	// biome-ignore lint/correctness/useExhaustiveDependencies: `agencyIntegrationsKey` intentionally stands in for the `agencyIntegrations` array reference (see above) — depending on the array itself defeats the memo.
	const availableAgencies = useMemo(() => getAvailableAgencies({ agencyIntegrations }), [agencyIntegrationsKey]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: same primitive stand-in as above; the map now reads `facilityId` off the rows, so it has to key on them too.
	const agencyFacilityIdMap = useMemo(() => getAgencyFacilityIdMap({ agencyIntegrations }), [agencyIntegrationsKey]);

	// The availability gate is the only place that knows an agency was configured and still not
	// offered. Reported once per department+agency per session — see `utils/agencySuppressionReports`,
	// which owns that bookkeeping (and its reset on logout) in a leaf module, so the logout path can
	// clear it without importing this hook and closing an AuthProvider/HospitalDataProvider cycle.
	// biome-ignore lint/correctness/useExhaustiveDependencies: `agencyIntegrationsKey` stands in for the array; re-running on its identity would re-enter this effect every render for no new information.
	useEffect(() => {
		for (const source of getSuppressedAgencies({ agencyIntegrations })) {
			if (!claimAgencySuppressionReport(chosenDepartment.department_id, source)) continue;
			track("Agency posting suppressed - integration inactive", { department_id: chosenDepartment.department_id, agency: source });
		}
	}, [chosenDepartment.department_id, agencyIntegrationsKey, track]);

	return { availableAgencies, agencyFacilityIdMap };
};
